import React, { useCallback } from 'react';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { REQUEST_TYPE_LABELS, REQUEST_TYPES } from '@/constants/request-types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Home,
  Build,
  Security,
  ReportProblem,
  Lightbulb,
  Architecture,
  HelpOutline,
  ArrowBack,
  ArrowForward,
  CheckCircle,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import { DatePicker } from '@mui/x-date-pickers';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z as zod } from 'zod';

import type { Unit } from '@/types/v2/unit';
import axios from '@/lib/v2/axios';
import { useUser } from '@/hooks/v2/use-user';
import { ProfessionalDialog } from '@/components/common';
import type { FileUpload, UploadedFile } from '@/components/common/ImageUploader';
import ImageUploader from '@/components/common/ImageUploader';

const TYPE_ICONS: Record<string, React.ReactNode> = {
  general: <HelpOutline sx={{ fontSize: 18 }} />,
  maintenance: <Build sx={{ fontSize: 18 }} />,
  incident: <ReportProblem sx={{ fontSize: 18 }} />,
  security: <Security sx={{ fontSize: 18 }} />,
  suggestions: <Lightbulb sx={{ fontSize: 18 }} />,
  arc: <Architecture sx={{ fontSize: 18 }} />,
};

const TYPE_HINTS: Record<string, string> = {
  general: 'General inquiries or other requests',
  maintenance: 'Repairs, maintenance, or service needs',
  incident: 'Incidents, accidents, or safety concerns',
  security: 'Security issues or suspicious activity',
  suggestions: 'Suggestions for community improvements',
  arc: 'Architectural changes requiring ARC review',
};

const STEPS = ['Type', 'Details', 'Attachments'];

const dateMessage = 'A date is required for incident or security requests';
const schema = zod
  .object({
    type: zod.enum(REQUEST_TYPES),
    subject: zod.string().min(1, 'A subject is required').max(200, 'Subject must be under 200 characters'),
    description: zod.string().min(1, 'A description is required').min(10, 'Description must be at least 10 characters'),
    files: zod
      .array(zod.object({ key: zod.string(), path: zod.string(), size: zod.number(), name: zod.string(), type: zod.string() }))
      .optional(),
    unit_id: zod
      .union([zod.string(), zod.number()])
      .optional()
      .transform((v) => (v === undefined || v === '' ? undefined : String(v))),
    experienced_on: zod.date({ message: dateMessage }).optional(),
    arc_project_description: zod.string().optional(),
    arc_start_date: zod.date().optional(),
    arc_completion_date: zod.date().optional(),
    arc_contractor_name: zod.string().optional(),
    arc_estimated_cost: zod.string().optional(),
  })
  .refine(
    (data) => {
      if (['incident', 'security'].includes(data.type)) return data.experienced_on !== undefined;
      return true;
    },
    { message: dateMessage, path: ['experienced_on'] }
  )
  .refine(
    (data) => {
      if (data.type === 'arc') return !!data.arc_project_description && data.arc_project_description.length >= 10;
      return true;
    },
    { message: 'A project description (min 10 chars) is required for ARC requests', path: ['arc_project_description'] }
  );

type Values = zod.infer<typeof schema>;

const defaultValues: Values = {
  type: 'general',
  subject: '',
  description: '',
  files: [] as UploadedFile[],
  unit_id: '',
  experienced_on: undefined,
  arc_project_description: '',
  arc_start_date: undefined,
  arc_completion_date: undefined,
  arc_contractor_name: '',
  arc_estimated_cost: '',
};

interface CreateRequestDialogProps {
  open: boolean;
  onClose: () => void;
  unitId?: string;
  communityId?: string;
}

export default function CreateRequestDialog({ open, onClose, unitId, communityId }: CreateRequestDialogProps) {
  const { community, user } = useUser();
  const queryClient = useQueryClient();

  const accountRoleNames = (user?.account_roles ?? []).map((r) => r.name);
  const communityRoleNames = (user?.community_roles ?? []).map((r) => r.name);
  const isAccountPrivileged = accountRoleNames.some((name) =>
    [ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_OWNER, ACCOUNT_ROLES.ACCOUNT_MANAGER].includes(name as any)
  );
  const isCommunityManager = communityRoleNames.includes(COMMUNITY_ROLES.COMMUNITY_MANAGER);
  const isBoardMember = communityRoleNames.includes(COMMUNITY_ROLES.BOARD_MEMBER);
  const hasManagerPrivileges = isAccountPrivileged || isCommunityManager || isBoardMember;

  const [activeStep, setActiveStep] = React.useState(0);

  const effectiveCommunityId = communityId || community?.id;
  const effectiveCommunity = communityId ? null : community;

  const { data: fetchedCommunity } = useQuery({
    queryKey: ['community', communityId],
    queryFn: async () => {
      if (!communityId) return null;
      const res = await axios.get(`/api/v2/communities/${communityId}`);
      return res.data;
    },
    enabled: Boolean(communityId && !community),
  });
  const currentCommunity = fetchedCommunity || effectiveCommunity;

  const { data: specificUnit } = useQuery({
    queryKey: ['unit', unitId],
    queryFn: async () => {
      if (!unitId) return null;
      const res = await axios.get(`/api/v2/units/${unitId}`);
      return res.data;
    },
    enabled: Boolean(unitId && effectiveCommunityId),
  });

  const { data: fetchedUnits } = useQuery({
    queryKey: ['units', effectiveCommunityId],
    queryFn: async () => {
      const res = await axios.get(`/api/v2/units`, { params: { community_id: effectiveCommunityId } });
      return res.data;
    },
    enabled: Boolean(effectiveCommunityId),
  });

  const unitsList: Unit[] = React.useMemo(() => {
    if (Array.isArray(fetchedUnits)) return fetchedUnits as Unit[];
    if (fetchedUnits && Array.isArray((fetchedUnits as any).units)) return (fetchedUnits as any).units as Unit[];
    return [] as Unit[];
  }, [fetchedUnits]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
    reset,
    trigger,
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues });

  const requestType = watch('type');
  const needsIncidentDate = ['incident', 'security'].includes(requestType);
  const isArc = requestType === 'arc';

  React.useEffect(() => {
    if (unitId) {
      setValue('unit_id', String(unitId), { shouldValidate: true });
    } else if (!hasManagerPrivileges && unitsList.length === 1) {
      setValue('unit_id', String(unitsList[0].id), { shouldValidate: true });
    }
  }, [unitId, hasManagerPrivileges, unitsList, setValue]);

  const createRequestMutation = useMutation({
    mutationFn: async (data: Values) => {
      if (!hasManagerPrivileges && !unitId && unitsList.length === 0) {
        throw new Error('You are not associated with a unit. Contact your community manager.');
      }

      const payload: Record<string, any> = {
        type: data.type,
        subject: data.subject,
        description: data.description,
        community_id: String(effectiveCommunityId),
        files: data.files,
      };

      if (data.experienced_on) payload.experiencedOn = data.experienced_on;

      if (data.type === 'arc') {
        payload.arc_project_description = data.arc_project_description;
        if (data.arc_start_date) payload.arc_start_date = data.arc_start_date;
        if (data.arc_completion_date) payload.arc_completion_date = data.arc_completion_date;
        if (data.arc_contractor_name) payload.arc_contractor_name = data.arc_contractor_name;
        if (data.arc_estimated_cost) payload.arc_estimated_cost = data.arc_estimated_cost;
      }

      if (data.unit_id) payload.unit_id = String(data.unit_id);

      const response = await axios.post('/api/v1/requests', payload);
      return response.data;
    },
  });

  const onSubmit = useCallback(
    async (data: Values) => {
      try {
        const response = await createRequestMutation.mutateAsync(data);
        if (response?.error) return;
        toast.success(response.message, { description: 'Your request has been created successfully.' });
        queryClient.invalidateQueries({ queryKey: ['requests', effectiveCommunityId] });
        if (unitId) queryClient.invalidateQueries({ queryKey: ['unit-requests', unitId, effectiveCommunityId] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-summary', effectiveCommunityId] });
        handleClose();
      } catch (error: any) {
        toast.error(error?.response?.data?.message || error?.message || 'Error creating request');
      }
    },
    [createRequestMutation, queryClient, effectiveCommunityId, unitId]
  );

  const [fileUploads, setFileUploads] = React.useState<FileUpload[]>([]);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    contentRef.current?.scrollTo({ top: 0 });
  };

  const handleClose = () => {
    reset();
    setFileUploads([]);
    setActiveStep(0);
    createRequestMutation.reset();
    onClose();
  };

  const handleUploadComplete = (uploads: FileUpload[]) => {
    const successfulUploads = uploads.filter((u) => u.status === 'success' && u.uploadedFile).map((u) => u.uploadedFile!);
    setValue('files', successfulUploads, { shouldValidate: true });
  };

  const uploadsInProgress = fileUploads.some((u) => u.status === 'uploading');

  const displayUnit = React.useMemo(() => {
    if (!unitId) return null;
    return specificUnit || unitsList.find((u: Unit) => u.id === unitId);
  }, [unitId, specificUnit, unitsList]);

  const handleNext = async () => {
    if (activeStep === 0) {
      setActiveStep(1);
      scrollToTop();
    } else if (activeStep === 1) {
      const fields: (keyof Values)[] = ['subject', 'description'];
      if (needsIncidentDate) fields.push('experienced_on');
      if (isArc) fields.push('arc_project_description');
      const valid = await trigger(fields);
      if (valid) {
        setActiveStep(2);
        scrollToTop();
      }
    }
  };

  const handleBack = () => {
    setActiveStep((s) => Math.max(0, s - 1));
    scrollToTop();
  };

  const stepper = (
    <Stepper
      activeStep={activeStep}
      alternativeLabel
      sx={{
        px: { xs: 2.5, sm: 3 },
        py: 1.5,
        '& .MuiStepLabel-label': { fontSize: '0.75rem', fontWeight: 500 },
        '& .MuiStepLabel-label.Mui-active': { fontWeight: 700 },
        '& .MuiStepLabel-root': { cursor: 'default' },
      }}
    >
      {STEPS.map((label) => (
        <Step key={label}>
          <StepLabel>{label}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );

  return (
    <ProfessionalDialog
      maxWidth="sm"
      onClose={handleClose}
      open={open}
      title="New Request"
      subtitle={currentCommunity?.name || 'Your community'}
      headerExtra={stepper}
      contentRef={contentRef}
      actions={
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button
            onClick={activeStep === 0 ? handleClose : handleBack}
            variant="outlined"
            size="small"
            startIcon={activeStep > 0 ? <ArrowBack sx={{ fontSize: '16px !important' }} /> : undefined}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {activeStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          {activeStep < 2 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              size="small"
              endIcon={<ArrowForward sx={{ fontSize: '16px !important' }} />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Continue
            </Button>
          ) : (
            <LoadingButton
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              size="small"
              loading={uploadsInProgress || createRequestMutation.isPending}
              disabled={uploadsInProgress || createRequestMutation.isPending}
              startIcon={<CheckCircle sx={{ fontSize: '16px !important' }} />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Submit Request
            </LoadingButton>
          )}
        </Box>
      }
    >
      {/* ── Step 0: Type ────────────────────────────────────── */}
      {activeStep === 0 && (
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            Select a request type
          </Typography>

          <Stack spacing={0}>
            {REQUEST_TYPES.map((type, idx) => {
              const selected = requestType === type;
              return (
                <Box
                  key={type}
                  onClick={() => setValue('type', type, { shouldValidate: true })}
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    cursor: 'pointer',
                    borderTop: idx === 0 ? '1px solid' : 'none',
                    borderBottom: '1px solid',
                    borderLeft: '1px solid',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    ...(idx === 0 && { borderTopLeftRadius: 8, borderTopRightRadius: 8 }),
                    ...(idx === REQUEST_TYPES.length - 1 && { borderBottomLeftRadius: 8, borderBottomRightRadius: 8 }),
                    bgcolor: selected ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
                    transition: 'background-color 0.1s',
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: selected ? 'primary.main' : 'text.secondary',
                      flexShrink: 0,
                    }}
                  >
                    {TYPE_ICONS[type]}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {REQUEST_TYPE_LABELS[type]}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {TYPE_HINTS[type]}
                    </Typography>
                  </Box>
                  {selected && (
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
                  )}
                </Box>
              );
            })}
          </Stack>
        </Stack>
      )}

      {/* ── Step 1: Details ───────────────────────────────────── */}
      {activeStep === 1 && (
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={REQUEST_TYPE_LABELS[requestType]} size="small" variant="outlined" />
            {watch('unit_id') && unitsList.find((u) => String(u.id) === String(watch('unit_id'))) && (
              <Typography variant="caption" color="text.secondary">
                Unit {unitsList.find((u) => String(u.id) === String(watch('unit_id')))?.unit_number || unitsList.find((u) => String(u.id) === String(watch('unit_id')))?.address}
              </Typography>
            )}
          </Stack>

          {/* Unit selector — moved from Step 0 */}
          {unitId ? (
            displayUnit && (
              <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Home sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {displayUnit.unit_number ? `Unit ${displayUnit.unit_number} — ` : ''}{displayUnit.address}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {displayUnit.city}, {displayUnit.state} {displayUnit.zip_code}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {hasManagerPrivileges
                  ? isArc ? 'Assign to a unit (required for ARC requests)' : 'Assign to a unit (optional — leave empty for community-level requests)'
                  : 'Select your unit'}
              </Typography>
              <Autocomplete
                getOptionLabel={(option: Unit) =>
                  `${option.unit_number ? `${option.unit_number} — ` : ''}${option.address}, ${option.city}, ${option.state}`
                }
                isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                onChange={(_, newValue: Unit | null) => {
                  setValue('unit_id', newValue ? String(newValue.id) : '', { shouldValidate: true });
                }}
                options={unitsList}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search by address or unit number..."
                    size="small"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack sx={{ py: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.unit_number ? `${option.unit_number} — ` : ''}{option.address}
                      </Typography>
                      <Typography color="text.secondary" variant="caption">
                        {option.city}, {option.state} {option.zip_code}
                      </Typography>
                    </Stack>
                  </li>
                )}
                slotProps={{
                  paper: { sx: { maxHeight: 280 } },
                  listbox: { sx: { maxHeight: 280 } },
                }}
                value={unitsList.find((u) => String(u.id) === String(watch('unit_id'))) ?? null}
              />
              {!hasManagerPrivileges && unitsList.length === 0 && (
                <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 1 }}>
                  You are not associated with any units. Contact your community manager.
                </Alert>
              )}
            </Box>
          )}

          <TextField
            fullWidth
            label="Subject"
            placeholder="Brief summary of your request..."
            {...register('subject')}
            error={Boolean(errors.subject)}
            helperText={errors.subject?.message || `${watch('subject')?.length || 0}/200`}
            inputProps={{ maxLength: 200 }}
          />

          <TextField
            fullWidth
            label="Description"
            multiline
            rows={4}
            placeholder="Provide details about what you need..."
            {...register('description')}
            error={Boolean(errors.description)}
            helperText={errors.description?.message}
          />

          {needsIncidentDate && (
            <Controller
              control={control}
              name="experienced_on"
              render={({ field }) => (
                <DatePicker<Dayjs>
                  label="When did this occur?"
                  maxDate={dayjs()}
                  onChange={(v: Dayjs | null) => field.onChange(v ? v.toDate() : null)}
                  value={field.value ? dayjs(field.value) : null}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(errors.experienced_on),
                      helperText: errors.experienced_on?.message || 'Required for incident and security reports',
                    },
                  }}
                />
              )}
            />
          )}

          {isArc && (
            <Stack spacing={2} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                ARC Review Details
              </Typography>
              <TextField
                fullWidth
                label="Project Description"
                multiline
                rows={3}
                placeholder="Describe the exterior change, addition, or modification..."
                {...register('arc_project_description')}
                error={Boolean(errors.arc_project_description)}
                helperText={errors.arc_project_description?.message}
              />
              <Grid container spacing={2}>
                <Grid xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="arc_start_date"
                    render={({ field }) => (
                      <DatePicker<Dayjs>
                        label="Planned Start"
                        minDate={dayjs()}
                        onChange={(v: Dayjs | null) => field.onChange(v ? v.toDate() : null)}
                        value={field.value ? dayjs(field.value) : null}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    )}
                  />
                </Grid>
                <Grid xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="arc_completion_date"
                    render={({ field }) => (
                      <DatePicker<Dayjs>
                        label="Expected Completion"
                        minDate={dayjs()}
                        onChange={(v: Dayjs | null) => field.onChange(v ? v.toDate() : null)}
                        value={field.value ? dayjs(field.value) : null}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contractor / Company"
                    placeholder="Optional"
                    size="small"
                    {...register('arc_contractor_name')}
                  />
                </Grid>
                <Grid xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Estimated Cost"
                    placeholder="e.g. $5,000"
                    size="small"
                    {...register('arc_estimated_cost')}
                  />
                </Grid>
              </Grid>
            </Stack>
          )}
        </Stack>
      )}

      {/* ── Step 2: Attachments ───────────────────────────────── */}
      {activeStep === 2 && (
        <Stack spacing={2.5}>
          {/* Review summary */}
          <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Summary
            </Typography>
            <Stack spacing={0.5}>
              <SummaryRow label="Type" value={REQUEST_TYPE_LABELS[requestType]} />
              <SummaryRow
                label="Scope"
                value={
                  unitId || watch('unit_id')
                    ? `Unit ${(displayUnit || unitsList.find((u) => String(u.id) === String(watch('unit_id'))))?.unit_number || ''}`
                    : isArc ? `Unit ${(displayUnit || unitsList.find((u) => String(u.id) === String(watch('unit_id'))))?.unit_number || ''}` : 'Community'
                }
              />
              <SummaryRow label="Subject" value={watch('subject') || '—'} />
              {isArc && <SummaryRow label="Project" value={watch('arc_project_description') || '—'} />}
              {needsIncidentDate && watch('experienced_on') && (
                <SummaryRow label="Incident Date" value={dayjs(watch('experienced_on')).format('MMM D, YYYY')} />
              )}
            </Stack>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {isArc
                ? 'Upload plans, drawings, photos, or supporting documents for the ARC review.'
                : 'Attach any relevant photos, documents, or files.'}
            </Typography>
            <ImageUploader
              maxFiles={isArc ? 10 : 5}
              onUploadComplete={handleUploadComplete}
              subtitle="Drag files here or click to browse"
              title=""
            />
          </Box>
        </Stack>
      )}
    </ProfessionalDialog>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Stack>
  );
}
